/**
 * Menu de dispositivos da seta ao lado do microfone e da câmera. O microfone lista também os
 * alto-falantes (só onde o navegador deixa escolher a saída); a câmera lista as câmeras.
 *
 * A lista é lida ANTES de o menu abrir (os hooks moram fora do conteúdo do menu). Se morassem dentro, o
 * menu abriria pequeno ("Nenhum dispositivo encontrado") e cresceria quando a lista chegasse, e um menu
 * que cresce depois de aberto precisa ser reposicionado: na janela flutuante isso falhava e ele cobria o
 * botão que o abriu.
 *
 * Escolher um dispositivo o torna o PREFERIDO (guardado neste navegador, `devicePreference.js`): a sala entra com ele nas
 * próximas chamadas, e o menu o marca com "Padrão". "Usar o padrão do navegador" limpa a preferência.
 */
import { useMediaDeviceSelect } from '@livekit/components-react';
import { ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { BackgroundEffectsGroup } from './BackgroundEffectsGroup';
import { FOCUS_RING, SIZE } from './controlStyles';
import { RoomMenu, RoomMenuContent, RoomMenuItem, RoomMenuLabel, RoomMenuRadioGroup, RoomMenuRadioItem, RoomMenuSeparator, RoomMenuTrigger } from './roomMenu';
import { useDevicePreference } from './useDevicePreference';

const FALLBACK_LABEL = Object.freeze({ videoinput: 'Câmera', audioinput: 'Microfone', audiooutput: 'Alto-falante' });

const TRIGGER_LABEL = Object.freeze({ audioinput: 'Escolher microfone', videoinput: 'Escolher câmera' });

/** Id do dispositivo ativo; se o navegador não listar o id atual (ex.: "default"), cai no primeiro. */
export function activeDeviceOf(devices, activeDeviceId) {
    return devices.some((d) => d.deviceId === activeDeviceId) ? activeDeviceId : (devices[0]?.deviceId ?? '');
}

/** Etiqueta do dispositivo preferido (o que a sala usa ao entrar). */
function DefaultTag() {
    return <span className="ml-2 rounded bg-white/10 px-1.5 py-0.5 align-middle text-[10px] font-semibold uppercase tracking-wide text-neutral-300">Padrão</span>;
}

/**
 * Um grupo do menu, sem hooks: recebe a lista pronta. `savedDeviceId` é o preferido (ganha a etiqueta "Padrão"); com ele
 * guardado, `onUseDefault` aparece como a saída para voltar ao padrão do navegador.
 */
export function DeviceGroupView({ kind, heading, devices, activeDeviceId, onSelect, first = true, savedDeviceId = null, onUseDefault }) {
    return (
        <>
            {!first && <RoomMenuSeparator />}
            <RoomMenuLabel>{heading}</RoomMenuLabel>
            {devices.length === 0 ? (
                <p className="px-3 py-2 text-sm text-neutral-400">Nenhum dispositivo encontrado</p>
            ) : (
                <RoomMenuRadioGroup value={activeDeviceOf(devices, activeDeviceId)} onValueChange={onSelect} aria-label={heading}>
                    {devices.map((device, index) => (
                        <RoomMenuRadioItem key={device.deviceId} value={device.deviceId}>
                            {device.label || `${FALLBACK_LABEL[kind]} ${index + 1}`}
                            {device.deviceId === savedDeviceId && <DefaultTag />}
                        </RoomMenuRadioItem>
                    ))}
                </RoomMenuRadioGroup>
            )}
            {savedDeviceId && onUseDefault && (
                <RoomMenuItem onSelect={onUseDefault} className="text-neutral-300">
                    Usar o padrão do navegador
                </RoomMenuItem>
            )}
        </>
    );
}

/** Menu sem hooks: recebe os grupos prontos (cada um com a lista de dispositivos já lida). `footer` vem depois dos grupos. */
function DeviceMenuView({ kind, variant, groups, footer, children }) {
    return (
        <RoomMenu>
            <RoomMenuTrigger asChild>
                <button
                    type="button"
                    aria-label={TRIGGER_LABEL[kind]}
                    className={cn('flex items-center justify-center border-l border-white/10 hover:bg-white/10', SIZE[variant].chevron, FOCUS_RING)}
                >
                    <ChevronUp className="h-4 w-4" aria-hidden="true" />
                </button>
            </RoomMenuTrigger>
            <RoomMenuContent align="center">
                {children ?? groups.map((group, index) => (
                    <DeviceGroupView key={group.kind} first={index === 0} {...group} />
                ))}
                {footer}
            </RoomMenuContent>
        </RoomMenu>
    );
}

/**
 * A lista de um tipo de dispositivo mais a preferência dele. Escolher troca o dispositivo ativo E o guarda como preferido;
 * "usar o padrão" limpa a preferência e volta ao dispositivo padrão do navegador (o id "default" vale para os três tipos).
 */
function useDeviceGroup(kind, heading) {
    const { devices, activeDeviceId, setActiveMediaDevice } = useMediaDeviceSelect({ kind });
    const { savedId, save, clear } = useDevicePreference(kind);
    return {
        kind,
        heading,
        devices,
        activeDeviceId,
        savedDeviceId: savedId,
        onSelect: (deviceId) => {
            save(deviceId);
            setActiveMediaDevice(deviceId);
        },
        onUseDefault: () => {
            clear();
            setActiveMediaDevice('default');
        },
    };
}

function MicrophoneMenu(props) {
    const microphones = useDeviceGroup('audioinput', 'Microfone');
    const speakers = useDeviceGroup('audiooutput', 'Alto-falante');
    const groups = [microphones];
    // Só onde o navegador deixa escolher a saída (o Safari, por exemplo, não lista alto-falantes).
    if (speakers.devices.length > 0) groups.push(speakers);
    return <DeviceMenuView {...props} groups={groups} />;
}

function CameraMenu(props) {
    const cameras = useDeviceGroup('videoinput', 'Câmera');
    // Depois das câmeras, o fundo (some onde o navegador não roda o efeito).
    return <DeviceMenuView {...props} groups={[cameras]} footer={<BackgroundEffectsGroup />} />;
}

/** @param {{ kind: 'audioinput' | 'videoinput', variant: keyof typeof SIZE, children?: React.ReactNode }} props */
export function DeviceMenu({ kind, ...props }) {
    return kind === 'audioinput' ? <MicrophoneMenu kind={kind} {...props} /> : <CameraMenu kind={kind} {...props} />;
}
